import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarIcon, Camera, Plus, Save, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { MoneyInput } from "@/components/ui/money-input";
import { sanitizeDatetimeLocal } from "@/lib/datetimeLocal";
import { PriceWithAdjustment } from "@/components/proposals/PriceWithAdjustment";
import { openNativeDateTimePicker } from "@/lib/dateTimePicker";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import {
  createAdditionalRequestService,
  fetchAdditionalRequestServices,
  getAdditionalRequestService,
  updateAdditionalRequestService,
} from "@/lib/additionalRequestServicesApi";

interface AdditionalRequestsStepProps {
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

const AdditionalRequestsStep = ({ data, onChange, readOnly }: AdditionalRequestsStepProps) => {
  const isReadOnly = !!readOnly;
  const { toast } = useToast();
  const [requests, setRequests] = useState<any[]>(data.additionalRequests || []);
  const [catalogRequests, setCatalogRequests] = useState<any[]>([]);
  const [savingCatalogIndex, setSavingCatalogIndex] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const dateTimeInputClassName = "w-[220px] max-w-full";
  const maxImages = 3;
  const maxImageSize = 5 * 1024 * 1024;

  useEffect(() => {
    if (!Array.isArray(data.additionalRequests)) {
      setRequests([]);
      return;
    }
    const normalized = (data.additionalRequests || []).map((item: any) => ({
      ...item,
      request_images: Array.isArray(item?.request_images) ? item.request_images.slice(0, maxImages) : [],
    }));
    setRequests(normalized);
  }, [data.additionalRequests]);

  useEffect(() => {
    const loadCatalogRequests = async () => {
      try {
        const response = await fetchAdditionalRequestServices({
          page: 1,
          size: 1000,
          sortField: "request_title",
          sortOrder: "asc",
        });
        setCatalogRequests(response.items || []);
      } catch {
        setCatalogRequests([]);
      }
    };

    void loadCatalogRequests();
  }, []);

  const refreshCatalogRequests = async () => {
    const response = await fetchAdditionalRequestServices({
      page: 1,
      size: 1000,
      sortField: "request_title",
      sortOrder: "asc",
    });
    setCatalogRequests(response.items || []);
  };

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Failed to read image"));
      reader.readAsDataURL(file);
    });

  const addRequest = () => {
    if (isReadOnly) return;
    const newRequest = {
      additional_request_service_id: null,
      request_title: "",
      request_ticket_type: "",
      request_quantity: 1,
      request_price: 0,
      request_description: "",
      request_date: "",
      request_time: "",
      request_images: [],
    };
    const newRequests = [newRequest, ...(requests || [])];
    setRequests(newRequests);
    onChange({ ...data, additionalRequests: newRequests });
  };

  const removeRequest = (index: number) => {
    if (isReadOnly) return;
    const newRequests = requests.filter((_, i) => i !== index);
    setRequests(newRequests);
    onChange({ ...data, additionalRequests: newRequests });
  };

  const updateRequest = (index: number, field: string, value: any) => {
    if (isReadOnly) return;
    const newRequests = [...requests];
    newRequests[index] = { ...newRequests[index], [field]: value };
    setRequests(newRequests);
    onChange({ ...data, additionalRequests: newRequests });
  };

  const selectCatalogRequest = async (index: number, selectedId: string | number) => {
    if (isReadOnly) return;
    const idNumber = Number(selectedId);
    if (!Number.isFinite(idNumber)) return;

    try {
      const selected = await getAdditionalRequestService(idNumber);
      const newRequests = [...requests];
      newRequests[index] = {
        ...newRequests[index],
        additional_request_service_id: selected.id,
        request_title: selected.request_title || "",
        request_price: selected.request_price ?? 0,
        request_description: selected.request_description || "",
        request_images: Array.isArray(selected.request_images) ? selected.request_images.slice(0, maxImages) : [],
      };
      setRequests(newRequests);
      onChange({ ...data, additionalRequests: newRequests });
    } catch {
      toast({
        title: "Unable to load service",
        description: "Failed to load details for the selected request.",
        variant: "destructive",
      });
    }
  };

  const createCustomRequestTitle = (index: number, title: string) => {
    if (isReadOnly) return;
    const newRequests = [...requests];
    newRequests[index] = {
      ...newRequests[index],
      additional_request_service_id: null,
      request_title: title,
    };
    setRequests(newRequests);
    onChange({ ...data, additionalRequests: newRequests });
  };

  const formatDateForInput = (value: string | undefined) => {
    if (!value) return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return "";

    const pad = (n: number) => String(n).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  };

  const formatTimeForInput = (value: string | undefined) => {
    if (!value) return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    return trimmed.slice(0, 5);
  };

  const formatRequestDatetimeLocal = (requestDate?: string, requestTime?: string) => {
    const datePart = formatDateForInput(requestDate);
    if (!datePart) return "";
    const timePart = formatTimeForInput(requestTime) || "00:00";
    return `${datePart}T${timePart}`;
  };

  const updateRequestDateTime = (index: number, value: string) => {
    if (isReadOnly) return;
    const sanitized = sanitizeDatetimeLocal(value);
    const [datePart, rawTimePart] = sanitized.split("T");
    const timePart = (rawTimePart || "").slice(0, 5);

    const newRequests = [...requests];
    newRequests[index] = {
      ...newRequests[index],
      request_date: datePart || "",
      request_time: timePart || "",
    };
    setRequests(newRequests);
    onChange({ ...data, additionalRequests: newRequests });
  };

  const addRequestImages = async (index: number, fileList: FileList | null) => {
    if (isReadOnly || !fileList) return;

    const currentImages = Array.isArray(requests[index]?.request_images) ? requests[index].request_images : [];
    if (currentImages.length >= maxImages) {
      toast({
        title: "Limit reached",
        description: `You can upload up to ${maxImages} photos per request.`,
        variant: "destructive",
      });
      return;
    }

    const files = Array.from(fileList).slice(0, maxImages - currentImages.length);
    const encodedImages: string[] = [];

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid file",
          description: "Only image files are allowed.",
          variant: "destructive",
        });
        continue;
      }

      if (file.size > maxImageSize) {
        toast({
          title: "Image too large",
          description: "Each image must be up to 5MB.",
          variant: "destructive",
        });
        continue;
      }

      try {
        const encoded = await readFileAsDataUrl(file);
        encodedImages.push(encoded);
      } catch {
        toast({
          title: "Error reading image",
          description: "Failed to process one of the selected images.",
          variant: "destructive",
        });
      }
    }

    const newRequests = [...requests];
    newRequests[index] = {
      ...newRequests[index],
      request_images: [...currentImages, ...encodedImages].slice(0, maxImages),
    };
    setRequests(newRequests);
    onChange({ ...data, additionalRequests: newRequests });

    const input = fileInputRefs.current[index];
    if (input) {
      input.value = "";
    }
  };

  const removeRequestImage = (index: number, imageIndex: number) => {
    if (isReadOnly) return;
    const currentImages = Array.isArray(requests[index]?.request_images) ? requests[index].request_images : [];
    const updatedImages = currentImages.filter((_: string, i: number) => i !== imageIndex);
    updateRequest(index, "request_images", updatedImages);
  };

  const saveRequestToCatalog = async (index: number) => {
    if (isReadOnly) return;
    const current = requests[index];
    const title = typeof current?.request_title === "string" ? current.request_title.trim() : "";
    if (!title) {
      toast({
        title: "Request title is required",
        description: "Fill the request title before saving to catalog.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSavingCatalogIndex(index);
      const payload = {
        request_title: title,
        request_description:
          typeof current?.request_description === "string" && current.request_description.trim().length > 0
            ? current.request_description.trim()
            : null,
        request_price: Number.isFinite(Number(current?.request_price)) ? Number(current.request_price) : 0,
        request_images: Array.isArray(current?.request_images) ? current.request_images.slice(0, maxImages) : [],
      };

      let saved: any;
      const existingByTitle = catalogRequests.find((item) => {
        const itemTitle = typeof item?.request_title === "string" ? item.request_title.trim().toLowerCase() : "";
        return itemTitle.length > 0 && itemTitle === title.toLowerCase();
      });
      const targetId = existingByTitle?.id ?? (current?.additional_request_service_id ? Number(current.additional_request_service_id) : null);

      if (targetId) {
        saved = await updateAdditionalRequestService(targetId, payload);
      } else {
        saved = await createAdditionalRequestService(payload);
      }

      const newRequests = [...requests];
      newRequests[index] = {
        ...newRequests[index],
        additional_request_service_id: saved.id,
        request_title: saved.request_title ?? newRequests[index].request_title,
        request_description: saved.request_description ?? newRequests[index].request_description,
        request_price: saved.request_price ?? newRequests[index].request_price,
        request_images: Array.isArray(saved.request_images) ? saved.request_images.slice(0, maxImages) : newRequests[index].request_images,
      };
      setRequests(newRequests);
      onChange({ ...data, additionalRequests: newRequests });
      await refreshCatalogRequests();

      toast({
        title: "Saved to catalog",
        description: "Request saved successfully for reuse.",
      });
    } catch {
      toast({
        title: "Error saving to catalog",
        description: "Could not save this request to catalog.",
        variant: "destructive",
      });
    } finally {
      setSavingCatalogIndex(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Additional Requests</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add any additional special requests
          </p>
        </div>
        <Button type="button" size="sm" onClick={addRequest} disabled={isReadOnly}>
          <Plus className="mr-2 h-4 w-4" />
          Add Request
        </Button>
      </div>

      {requests.length === 0 ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
          onClick={isReadOnly ? undefined : addRequest}
          onKeyDown={isReadOnly ? undefined : (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addRequest();
            }
          }}
          role={isReadOnly ? undefined : "button"}
          tabIndex={isReadOnly ? -1 : 0}
          aria-label={isReadOnly ? undefined : "Add request"}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No additional requests added yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Request" to get started
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {requests.map((request, index) => (
            <div key={index} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-3 right-3 h-8 w-8 p-0"
                onClick={() => removeRequest(index)}
                disabled={isReadOnly}
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Request Title</Label>
                  <SearchableSelect
                    items={catalogRequests.map((item) => ({
                      id: item.id,
                      title: item.request_title,
                      description: item.request_description || undefined,
                    }))}
                    value={request.additional_request_service_id ?? undefined}
                    fallbackDisplayValue={!request.additional_request_service_id ? request.request_title || undefined : undefined}
                    onValueChange={(value) => void selectCatalogRequest(index, value)}
                    allowCreate={!isReadOnly}
                    onCreateOption={(value) => createCustomRequestTitle(index, value)}
                    createOptionLabel={(value) => `Use "${value}" as new request`}
                    placeholder="Search existing request"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Request Price</Label>
                  <MoneyInput
                    value={request.request_price}
                    onChange={(value) => {
                      updateRequest(index, "request_price", value ? parseFloat(value) : 0);
                    }}
                    className="w-[140px]"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Ticket Type</Label>
                  <Input
                    type="text"
                    value={request.request_ticket_type ?? ""}
                    onChange={(e) => updateRequest(index, "request_ticket_type", e.target.value)}
                    placeholder="e.g. VIP, Standard, Adult..."
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={request.request_quantity ?? 1}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value, 10);
                      updateRequest(index, "request_quantity", Number.isFinite(parsed) ? Math.max(1, parsed) : 1);
                    }}
                    className="w-[140px]"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    Request Date & Time
                  </Label>
                  <Input
                    type="datetime-local"
                    value={formatRequestDatetimeLocal(request.request_date, request.request_time)}
                    onChange={(e) => updateRequestDateTime(index, e.target.value)}
                    onClick={(e) => openNativeDateTimePicker(e.currentTarget)}
                    onFocus={(e) => openNativeDateTimePicker(e.currentTarget)}
                    className={dateTimeInputClassName}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Request Description</Label>
                  <Textarea
                    value={request.request_description}
                    onChange={(e) => updateRequest(index, "request_description", e.target.value)}
                    placeholder="Enter detailed description..."
                    className="min-h-[100px]"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="border-t pt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Request Photos</Label>
                  <span className="text-xs text-muted-foreground">
                    {(request.request_images || []).length}/{maxImages}
                  </span>
                </div>
                <input
                  ref={(element) => {
                    fileInputRefs.current[index] = element;
                  }}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => void addRequestImages(index, e.target.files)}
                  disabled={isReadOnly}
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {Array.from({ length: maxImages }).map((_, slotIndex) => {
                    const image = request.request_images?.[slotIndex];
                    if (image) {
                      return (
                        <div
                          key={`image-slot-${slotIndex}`}
                          className="relative h-36 rounded-lg border border-border bg-muted/20 overflow-hidden"
                        >
                          <img
                            src={image}
                            alt={`Request image ${slotIndex + 1}`}
                            className="h-full w-full object-cover"
                          />
                          {!isReadOnly && (
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute top-2 right-2 h-7 w-7"
                              onClick={() => removeRequestImage(index, slotIndex)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <button
                        key={`empty-slot-${slotIndex}`}
                        type="button"
                        className="h-36 rounded-lg border border-dashed border-border bg-muted/30 hover:bg-muted/50 transition-colors flex flex-col items-center justify-center text-muted-foreground disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={() => fileInputRefs.current[index]?.click()}
                        disabled={isReadOnly}
                      >
                        <Camera className="h-6 w-6 mb-2" />
                        <span className="text-xs font-medium">Add Photo</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="border-t pt-5 flex items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void saveRequestToCatalog(index)}
                  disabled={isReadOnly || savingCatalogIndex === index}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {request.additional_request_service_id ? "Update in Catalog" : "Save to Catalog"}
                </Button>
                <div className="text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Item total</p>
                  <PriceWithAdjustment
                    baseAmount={Number(request.request_price || 0) * Math.max(1, Number(request.request_quantity || 1))}
                    proposalData={data}
                    size="md"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdditionalRequestsStep;
